import { z } from "zod";

/**
 * Servers validation (Phase 5.3).
 *
 * Boundary validation for the create / update / tag / list flows. Rules mirror
 * the legacy client-side checks in `web/js/servers.js` + `dashboard.js`:
 *   • IP is always required.
 *   • Port defaults to "3003" (legacy default).
 *   • Type is "company" | "customer" (default "company").
 *   • When Pulseway is enabled, a Pulseway machine id is required.
 *   • Tags are de-duplicated case-insensitively (first casing wins).
 */

export const SERVER_TYPES = ["company", "customer"] as const;
export const serverTypeSchema = z.enum(SERVER_TYPES);

export const SERVER_SORT_COLUMNS = [
  "status",
  "name",
  "address",
  "type",
  "tags",
  "containers",
  "updates",
  "lastUpdate",
] as const;
export const serverSortColumnSchema = z.enum(SERVER_SORT_COLUMNS);
export const sortDirectionSchema = z.enum(["asc", "desc"]);

/** Normalize a tag list: trim, drop empties, dedupe case-insensitively. */
export function normalizeTags(tags: readonly unknown[] | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags ?? []) {
    const t = String(raw ?? "").trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

const tagsSchema = z
  .array(z.string())
  .max(50)
  .optional()
  .transform((v) => normalizeTags(v));

/**
 * A DNS hostname: dot-separated labels of alphanumerics and hyphens, no leading
 * or trailing hyphen, 1-63 chars per label. Deliberately strict — the value is
 * interpolated straight into `http://<ip>:<port>/api/containers`, so anything
 * that can carry a scheme, credentials, path, query, whitespace or a second host
 * is a request-forgery primitive, not a hostname.
 */
const HOSTNAME = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\.?$/i;

/**
 * The monitored host: an IPv4/IPv6 literal, or a hostname.
 *
 * SECURITY (F-5): this was `z.string().trim().min(1).max(255)` — any string at
 * all. Because `fetchContainers` builds `http://${ip}:${port}/api/containers`,
 * an Operator could steer that request anywhere by supplying a crafted value
 * (`evil.com#`, `[::1]`, `1.2.3.4@attacker.tld`, a value with a path or a
 * userinfo section) and the WUD Basic-auth credentials would be sent along with
 * it. Structural validation here is the first of two layers; the second is the
 * runtime SSRF guard in server.gateway.ts, which resolves the host and enforces
 * the allowed target ranges immediately before the request.
 */
const hostSchema = z
  .string()
  .trim()
  .min(1, "Server IP address is required")
  .max(253)
  .refine((v) => z.string().ip().safeParse(v).success || HOSTNAME.test(v), {
    message: "Must be a valid IPv4/IPv6 address or hostname",
  });

/**
 * The WUD port. Was `max(10)` on an arbitrary string, so ":3003/../../x" or
 * ":80#" passed. Digits only, within the valid port range.
 */
const portSchema = z
  .string()
  .trim()
  .max(5)
  .optional()
  .transform((v) => (v && v.length > 0 ? v : "3003"))
  .refine(
    (v) => /^\d{1,5}$/.test(v) && Number(v) >= 1 && Number(v) <= 65535,
    { message: "Port must be a number between 1 and 65535" },
  );

const optionalTrimmed = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined));

/**
 * POST /api/v1/servers (create) and PUT /api/v1/servers/:id (update).
 * `pulsewayId` is required when `pulsewayEnabled` is true (legacy rule).
 */
export const createServerSchema = z
  .object({
    name: optionalTrimmed(200),
    ip: hostSchema,
    port: portSchema,
    type: serverTypeSchema.default("company"),
    tags: tagsSchema,
    username: optionalTrimmed(200),
    // Password may legitimately contain surrounding characters; only cap length.
    password: z
      .string()
      .max(500)
      .optional()
      .transform((v) => (v && v.length > 0 ? v : undefined)),
    pulsewayEnabled: z.boolean().default(false),
    pulsewayId: optionalTrimmed(200),
  })
  .superRefine((data, ctx) => {
    if (data.pulsewayEnabled && !data.pulsewayId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pulsewayId"],
        message: "Pulseway machine ID is required when Pulseway is enabled",
      });
    }
  });

export type CreateServerInput = z.infer<typeof createServerSchema>;

export const updateServerSchema = createServerSchema;
export type UpdateServerInput = z.infer<typeof updateServerSchema>;

/** POST /api/v1/servers/tags/rename. */
export const renameTagSchema = z
  .object({
    from: z.string().trim().min(1, "The tag to rename is required").max(100),
    to: z.string().trim().min(1, "The new tag name is required").max(100),
  })
  .refine((d) => d.from.toLowerCase() !== d.to.toLowerCase(), {
    message: "The new tag must be different from the old tag",
    path: ["to"],
  });

export type RenameTagInput = z.infer<typeof renameTagSchema>;

/** POST /api/v1/servers/tags/delete. */
export const deleteTagSchema = z.object({
  tag: z.string().trim().min(1, "The tag to delete is required").max(100),
});

export type DeleteTagInput = z.infer<typeof deleteTagSchema>;

/** GET /api/v1/servers querystring. */
export const serverListQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  type: z.union([serverTypeSchema, z.literal("")]).optional(),
  tag: z.string().trim().max(100).optional(),
  sort: serverSortColumnSchema.optional(),
  direction: sortDirectionSchema.optional(),
});

export type ServerListQueryInput = z.infer<typeof serverListQuerySchema>;
